import { Info, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Compact admin page header: one row – title (+ optional info tooltip and status chips) left, actions right.
 * The long explanation lives behind the «i», so the page itself starts right away.
 * On mobile the actions drop to their own row and stretch.
 */
export const PageHeader = ({ eyebrow, title, description, actions, secondary, status, children, className, testId = "admin-page-header" }) => (
  <header className={cn("hairline mb-4 border-b pb-3.5 sm:mb-5 sm:pb-4", className)} data-testid={testId}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-2">
        {eyebrow && <span className="t-mono-sm shrink-0 text-muted-foreground">{eyebrow}</span>}
        <h1 className="min-w-0 truncate font-display text-[19px] font-semibold tracking-[-0.02em] sm:text-[20px]" data-testid="admin-page-title">{title}</h1>
        {description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground" aria-label="Om denne sida" data-testid="admin-page-info"><Info className="h-4 w-4" strokeWidth={1.75} /></button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" className="max-w-xs text-[12.5px] leading-relaxed">{description}</TooltipContent>
          </Tooltip>
        )}
        {status && <div className="ml-1 hidden items-center gap-2 md:flex" data-testid="admin-page-status">{status}</div>}
      </div>
      {(actions || secondary) && (
        <div className="flex shrink-0 items-center gap-2 [&>*:not([data-icon-action])]:flex-1 sm:[&>*]:flex-none" data-testid="admin-page-actions">
          {secondary}
          {actions}
        </div>
      )}
    </div>
    {status && <div className="mt-2 flex flex-wrap items-center gap-2 md:hidden">{status}</div>}
    {children && <div className="mt-3" data-testid="admin-page-header-tabs">{children}</div>}
  </header>
);

/** “Fleire”-menu for secondary page actions. items: [{ label, icon, onClick, destructive, disabled, testId }] */
export const SecondaryMenu = ({ items = [], label = "Fleire handlingar", testId = "page-header-secondary-menu" }) => {
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 border-border/70 bg-card" aria-label={label} data-icon-action="true" data-testid={testId}><MoreHorizontal className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-popover">
        {items.map((it, i) => (
          it === "separator" ? <DropdownMenuSeparator key={`sep-${i}`} /> : (
            <DropdownMenuItem key={it.label} onSelect={it.onClick} disabled={it.disabled} className={cn(it.destructive && "text-destructive focus:text-destructive")} data-testid={it.testId}>
              {it.icon && <it.icon className="h-4 w-4" strokeWidth={1.75} />} {it.label}
            </DropdownMenuItem>
          )
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Section heading inside a page (between blocks). */
export const SectionHeading = ({ title, description, action, className, testId }) => (
  <div className={cn("mb-3 flex items-end justify-between gap-3", className)} data-testid={testId}>
    <div className="min-w-0">
      <h2 className="font-display text-[14px] font-semibold tracking-[-0.01em]">{title}</h2>
      {description && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{description}</p>}
    </div>
    {action && <div className="shrink-0 whitespace-nowrap">{action}</div>}
  </div>
);
